import {
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Copy,
    Download,
    Loader2,
    Sparkles,
    Wand2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css"; // Import KaTeX CSS for LaTeX styling
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { AIAnalysisResult, TrialData } from "../types/api";
import { DateFormats, formatLocalDateWithTimezone } from "../utils/dateUtils";
import { logger } from "../utils/logger";

interface AIInsightsProps {
  studyId: string;
  studyName?: string;
  trials: TrialData[];
  onAnalysisGenerated?: (analysis: AIAnalysisResult) => void;
}

export default function AIInsights({
  studyId,
  studyName,
  trials,
  onAnalysisGenerated,
}: AIInsightsProps) {
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isGeneratingStudy, setIsGeneratingStudy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Persist collapsed state per study in localStorage
  const getStorageKey = () => `ai-insights-collapsed-${studyId}`;

  const getInitialCollapsedState = () => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  };

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);

  const api = useApi();
  const navigate = useNavigate();
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Load existing insights when component mounts or studyId changes
  useEffect(() => {
    const loadExistingInsights = async () => {
      if (!studyId) return;

      setIsLoadingExisting(true);
      setError(null);

      try {
        const existingInsights = await api.getExistingInsights(studyId);
        if (existingInsights) {
          setAnalysis(existingInsights);
          if (onAnalysisGenerated) {
            onAnalysisGenerated(existingInsights);
          }
        }
      } catch (err) {
        // Silently fail - this just means no existing insights
        console.log("No existing insights found for study:", studyId);
      } finally {
        setIsLoadingExisting(false);
      }
    };

    loadExistingInsights();
  }, [studyId, api, onAnalysisGenerated]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  // Update localStorage when collapsed state changes
  const handleToggleCollapsed = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(newCollapsedState));
    } catch {
      // Silently fail if localStorage is not available
    }
  };

  const generateInsights = async (forceRegenerate: boolean = false) => {
    setIsGenerating(true);
    setError(null);

    // Auto-expand panel on first generation so user can see the process
    if (!analysis && isCollapsed) {
      setIsCollapsed(false);
      try {
        localStorage.setItem(getStorageKey(), JSON.stringify(false));
      } catch {
        // Silently fail if localStorage is not available
      }
    }

    try {
      const analysisResult = await api.generateAiInsights(
        studyId,
        trials,
        forceRegenerate,
      );
      setAnalysis(analysisResult);

      if (onAnalysisGenerated) {
        onAnalysisGenerated(analysisResult);
      }
    } catch (err) {
      const e = err as { status?: number; message?: string };
      console.error("Error generating insights:", err);

      if (e.status === 429) {
        setError(
          "You have generated too many insights. Please try again in an hour.",
        );
      } else if (e.status === 402) {
        setError(
          "You have exceeded your monthly AI insights quota. Please upgrade your plan.",
        );
      } else if (e.status === 503 || e.status === 504) {
        setError(
          "The AI service is temporarily unavailable. Please try again later.",
        );
      } else if (e.status === 401) {
        setError("Please sign in to generate AI insights.");
      } else {
        setError(e.message || "Failed to generate insights. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (analysis) {
      navigator.clipboard.writeText(analysis.analysis);
    }
  };

  const handleDownloadMarkdown = () => {
    if (!analysis) return;
    const blob = new Blob([analysis.analysis], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-insights-${studyId}.md`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleDownloadPDF = async () => {
    if (!analysis) return;

    try {
      // Dynamic import to keep bundle size down
      const { exportAIInsightsAsPDF } = await import("../utils/export/aiInsightsPdf");
      await exportAIInsightsAsPDF({
        studyId,
        studyName: studyName || `Study ${studyId.slice(0, 8)}`,
        markdown: analysis.analysis,
        generatedAt: analysis.generated_at,
        trialsAnalyzed: analysis.trials_analyzed,
        modelVersion: analysis.model_version,
      });
      setShowExportMenu(false);
    } catch (err) {
      console.error("Error exporting PDF:", err);
      setError(err instanceof Error ? err.message : "Failed to export PDF");
    }
  };

  const handleCreateStudy = async () => {
    if (!analysis || !studyId) return;

    setIsGeneratingStudy(true);
    setError(null);

    try {
      logger.info("Generating study refinement from AI insights...");
      logger.info(`Refining study from insights: ${studyId}`);

      const studyConfig = await api.refineStudyFromInsights(
        studyId,
        trials,
      );

      logger.info("Study refinement generated, navigating to new study page");

      // Navigate to new study page with pre-populated config
      navigate("/new-study", {
        state: {
          prefilledConfig: studyConfig,
          sourceStudyId: studyId,
        },
      });
    } catch (err: any) {
      logger.error("Failed to refine study", { error: err });
      setError(
        err.message ||
          "Failed to refine study. Please try again.",
      );
    } finally {
      setIsGeneratingStudy(false);
    }
  };

  return (
    <div className="bg-slate-900/60 rounded-2xl border border-slate-700/50 shadow-2xl shadow-black/20 mb-6 hover:shadow-glow transition-all duration-300">
      {/* Header */}
      <div
        className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-800/30 transition-colors rounded-t-2xl"
        onClick={handleToggleCollapsed}
      >
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">
            ✨ AI-Powered Insights
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {analysis
              ? "Analysis results complete"
              : "Generate intelligent analysis of your optimization results"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Force regenerate if analysis already exists
              generateInsights(!!analysis);
            }}
            disabled={isGenerating || isLoadingExisting}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700/60 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating || isLoadingExisting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {isLoadingExisting ? "Loading..." : "Analyzing..."}
              </>
            ) : (
              <>
                <Sparkles size={16} />
                {analysis ? "Re-Generate" : "Generate"}
              </>
            )}
          </button>

          {/* NEW BUTTON: Refine Study from Recommendations */}
          {analysis && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCreateStudy();
              }}
              disabled={isGeneratingStudy || isGenerating}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-3 py-1.5 text-sm text-white hover:from-violet-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
              title="Refine study by narrowing search based on top-performing trials"
            >
              {isGeneratingStudy ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Refine Study
                </>
              )}
            </button>
          )}

          {/* Existing Copy and Download buttons */}
          {analysis && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className="p-2 rounded-xl hover:bg-slate-700/50 transition-colors"
                aria-label="Copy markdown"
              >
                <Copy size={16} className="text-slate-300" />
              </button>

              {/* Export dropdown */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExportMenu(!showExportMenu);
                  }}
                  className="p-2 rounded-xl hover:bg-slate-700/50 transition-colors"
                  aria-label="Export options"
                >
                  <Download size={16} className="text-slate-300" />
                </button>

                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-xl border border-slate-700 bg-slate-900 shadow-xl z-50">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF();
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 rounded-t-xl transition-colors flex items-center gap-2"
                    >
                      <Download size={14} />
                      Export as PDF
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadMarkdown();
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 rounded-b-xl transition-colors flex items-center gap-2"
                    >
                      <Download size={14} />
                      Export as Markdown
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleCollapsed();
            }}
            className="p-2 rounded-xl hover:bg-slate-700/50 transition-colors"
          >
            {isCollapsed ? (
              <ChevronDown size={16} className="text-slate-400" />
            ) : (
              <ChevronUp size={16} className="text-slate-400" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-6 pb-6">
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-600/50 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-medium">
                  Error generating insights
                </p>
                <p className="text-red-400 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {analysis && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-slate-400">
                <span>
                  Generated{" "}
                  {formatLocalDateWithTimezone(analysis.generated_at, DateFormats.COMPACT)}
                </span>
                <span>Analyzed {analysis.trials_analyzed} trials</span>
              </div>

              <div className="rounded-xl border border-slate-700/50 bg-gradient-to-b from-slate-900/60 to-slate-900/30 p-6">
                <div
                  className="prose prose-invert prose-lg max-w-none 
                  prose-headings:text-slate-100 
                  prose-h1:text-3xl prose-h1:text-blue-300 prose-h1:border-b prose-h1:border-blue-500/30 prose-h1:pb-2
                  prose-h2:text-2xl prose-h2:text-emerald-300 prose-h2:mt-8
                  prose-h3:text-xl prose-h3:text-yellow-300 prose-h3:mt-6
                  prose-h4:text-lg prose-h4:text-rose-300
                  prose-p:text-slate-200/90 prose-p:leading-relaxed
                  prose-strong:text-emerald-300 prose-strong:font-semibold
                  prose-em:text-yellow-300 prose-em:italic
                  prose-ul:text-slate-200/90 prose-ol:text-slate-200/90
                  prose-li:marker:text-slate-400
                  prose-blockquote:border-l-4 prose-blockquote:border-blue-500/50 prose-blockquote:bg-slate-800/50 prose-blockquote:p-4 prose-blockquote:rounded-r
                  prose-code:text-pink-300 prose-code:bg-slate-800/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-slate-800/50 prose-pre:border prose-pre:border-slate-600/50
                  prose-table:text-slate-200
                  prose-th:border-slate-600 prose-th:bg-slate-800/50
                  prose-td:border-slate-600
                "
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{
                      code: ({ children, ...props }) => (
                        <span
                          className="text-blue-300 bg-slate-800/50 px-1 py-0.5 rounded font-medium border border-blue-500/30"
                          {...props}
                        >
                          {children}
                        </span>
                      ),
                    }}
                  >
                    {analysis.analysis}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}

          {!analysis && !error && !isLoadingExisting && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 mb-4">
                <Sparkles className="h-8 w-8 text-indigo-400" />
              </div>
              <p className="text-slate-300 text-lg mb-2">
                Click "Generate" to analyze your optimization results with AI
              </p>
            </div>
          )}

          {isLoadingExisting && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 mb-4">
                <Loader2 className="h-8 w-8 text-indigo-400 animate-spin" />
              </div>
              <p className="text-slate-300 text-lg mb-2">
                Loading existing insights...
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
